import { Fragment, ReactElement, useEffect, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import useApiState, { api } from "../../hooks/use-api-state";
import { IGetLeaderboardResponse, IRewards, ILab, ILabAccomplishment, IAchievement, ISetManualPointsResponse} from "../../messages/IGetCohortMemberListResponse";
import Loading from "../common/Loading";
import { IAppInfo, IUserInfo } from "../../messages/IAppInfo";
import ErrorNotification from "../common/ErrorNotification";
import styles from "./Leaderboard.module.css";
import { ICohortData, ICtfDataResponse, ISubCohort } from "../../messages/IGetCohortByIdResponse";
import { hasPermission } from "../common/ProtectedRoute";


export default function Leaderboard(props: {
    appInfo: IAppInfo
    userInfo: IUserInfo;
}): ReactElement {
    const effectRunCount = useRef(0); // To detect multiple invocations of useEffect in Strict development mode
    const [apiState, setApiState] = useApiState(false);
    const [noApiState, setNoApiState] = useApiState(false);
    const [multiplierState, setMultiplierState] = useApiState(false);
    const [startStopApiState, setStartStopApiState] = useApiState(false);
    //const [openMembers, setOpenMembers] = useState<{ [subcohortId: string]: boolean }>({});
    const [openLabs, setOpenLabs] = useState<{ [subcohortId: string]: boolean }>({});
    const [settingMultiplier, setSettingMultiplier] = useState<{lab_id: string, subcohort_id: string} | null>(null);

    //const [cohortId, setCohortId] = useState<string | null>(null);
    const [subcohortByPoints, setSubcohortByPoints] = useState<{ subcohort: ISubCohort; points: number }[]>([]); // For sorting subcohorts by points
    const [cohortData, setCohortData] = useState<ICohortData | null>(null);
    //const [subcohortMembers, setSubcohortMembers] = useState<{ [subcohortId: string]: string[] }>({});
    //const [memberData, setMemberData] = useState<{[user_id: string]: IGetCohortMemberData} | null>(null); // Stores all detailed data for each member, indexed by user_id
    const [labsList, setLabsList] = useState<ILab[] | null>(null);
    const [perSubcohortLabList, setPerSubcohortLabList] = useState<{ [subcohort_name: string]: ILab[] } | null>(null);
    const [rewards, setRewards] = useState<{ [subcohort: string]: IRewards } | null>(null);

    const [manualApiState, setManualApiState] = useApiState(false);
    const [addingMultiplier, setAddingMultiplier] = useState<{subcohort_id: string, lab_id: string} | null>(null);
    const [addingPoints, setAddingPoints] = useState<{subcohort_id: string, lab_id: string} | null>(null);
    const [editingPointsValue, setEditingPointsValue] = useState<string>("");


    useEffect(() => {
        document.title = "CTF leaderboard";
        // Get user's cohorts and cohort member's data
        effectRunCount.current += 1;
        if (effectRunCount.current===1) {
            getLeaderboardData("get");
            /*
            let cohort_code = null;
            for (const [key, value] of Object.entries(props.userInfo.user_cohort_data)) {
                if (!key.startsWith("#")) {
                    cohort_code = key;
                    break;
                }
            };

            api.getCohortByCode(
                setApiState,
                cohort_code ?? "")
            .then((cohort) => {
                setCohortData(cohort);
                getLeaderboardData(cohort);
            });
            */
        }
    }, [props.userInfo]);


    function getLeaderboardData(action: string, cohort:ICohortData|null=null, subcohort:string|null=null, apiStateSetter = setApiState) {
        api.getCohortLeaderboard(
            apiStateSetter,
            action,
            cohort ? cohort.id : null,
            subcohort)
        .then((ret: IGetLeaderboardResponse) => {
            //console.log(`getLeaderboardData cohortData=${ret?.cohort_data} cohortId=${ret?.cohort_data?.id}`);
            setCohortData(ret?.cohort_data);
            //setSubcohortMembers(ret?.member_ids_by_subcohort ?? {});
            //setMemberData(ret?.member_data ?? null);
            setLabsList(ret?.labs_list ?? null);
            setPerSubcohortLabList(ret?.per_subcohort_lab_list ?? null);
            setRewards(ret?.rewards ?? null);
            setSubcohortByPoints(ret?.cohort_data?.subcohorts.map((subcohort) => ({
                subcohort,
                points: ret?.rewards?.[subcohort.id]?.overall_points ?? 0,
            })).sort((a, b) => b.points - a.points));
        });

    }

    useEffect(() => {
        if (cohortData?.id) {
            connectToSqs();
        }
    }, [cohortData?.id]);


    function connectToSqs() {
        if (!cohortData) return;
        sqsutils.init("", 20, props.appInfo.csrf_token,
            () => {
                //console.log("Connected to SQS.");
            }, // connectedCallback
            (immediateReconnect) => {
                //console.log("Disconnected from SQS. Immediate reconnect:", immediateReconnect);
            }, // disconnectedCallback
            () => {
                //console.log("status changed SQS.");
            }, // statusChangeCallback
            (error)=>{
                console.log("Error connecting to the server. Please try again later.");
            }, // giveupCallback
            false);
        /*console.log(`Connecting to SQS... (cohortId=${cohortData.id}, subcohortId="all")`);*/
        /*console.log(`Connecting to message queue... ${cohortData.id}/all`);*/
        sqsutils.sqs_connect(cohortData.id, "all", true);
        sqsutils.registerChannelProcessor("all", (json: SQSMessage) => {
            /*console.log(`New cohort results: ${JSON.stringify(json)}`);*/
            console.log(`New cohort results, refreshing the leaderboard...`);
            getLeaderboardData("refresh", cohortData, null, setNoApiState);
        });
    }


    
    async function setCtfData(
        e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
        attribute: string,
        subcohortId: string,
        value: string
    ) {
        e.stopPropagation();
        if (!cohortData?.id)
            return;
        setSettingMultiplier({lab_id: value, subcohort_id: subcohortId});
        /*setMultiplierState({isLoading: true, errorMessage: null, isUnauthenticated: false});*/
        api.setCtfData(
            setMultiplierState,
            cohortData?.id,
            attribute,
            subcohortId,
            value,
        ).then((ctfDataResponse: ICtfDataResponse) => {
            //setSettingMultiplier(null);
            if (ctfDataResponse.status !== "ok") {
                if (ctfDataResponse.message) {
                    alert(ctfDataResponse.message);
                }
                return;
            }
            setCohortData((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    ctf_data: ctfDataResponse.ctf_data,
                };
            });
            getLeaderboardData("refresh", cohortData, subcohortId, setNoApiState);
        }).catch((error) => {
            //setSettingMultiplier(null);
            //console.error("Error setting CTF data:", error);
            alert("An error occurred while setting the score multiplier.");
        });
    }


    function getAchievementClass(achievement: IAchievement) {
        switch(achievement.type) {
            case "first_blood":
                return styles.achievement_first_blood;
            default:
                return "";
        }
    }

    function getScoreMultiplierClass(userSubcohort:string, subcohortId: string, lab: ILab, score_multiplier_lab_id: string|undefined|null, started: boolean, settingMultiplier: {lab_id: string, subcohort_id: string} | null): string {
        let style = styles.score_multiplier;
        if (userSubcohort===subcohortId) {
            style += ` ${styles.score_multiplier_own}`;
            if (!started) {
               style += ` ${styles.score_multiplier_can_click}`;
            }
        }
        else {
            if (lab.lab_id !== score_multiplier_lab_id) {
                style += ` ${styles.score_multiplier_notown}`;
            }
        }
        if (lab.lab_id === score_multiplier_lab_id) {
            style += ` ${styles.score_multiplier_active}`;
        }
        else if (settingMultiplier && settingMultiplier.lab_id === lab.lab_id && settingMultiplier.subcohort_id === subcohortId) {
            style += ` ${styles.score_multiplier_setting}`;
        } 
        else if (started) {
            style += ` ${styles.score_multiplier_started}`;
        }
        return style;
    }


    function countCounttags(achievements: IAchievement[], tags: string[]): number {
        if (!achievements) return 0;
        let count = 0;
        for (const achievement of achievements) {
            if (achievement.type === "counttags" && tags.includes(achievement.tag ?? "")) {
                count++;
            }
        }
        return count;
    }


    function startStopCtf(action: "start" | "stop") {
        if (!cohortData?.id)
            return;
        api.startStopCtf(
            setStartStopApiState,
            cohortData.id,
            action
        ).then((response: ICtfDataResponse) => {
            if (response.status === "ok") {
                setCohortData((prev) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        ctf_data: response.ctf_data,
                    };
                });
                //We will send an SQS message from the server, that will fix everything else
                //getLeaderboardData(cohortData, null, setNoApiState);
            } else {
                if (response.message) {
                    alert(response.message);
                }
            }
        });
    }


    function setManualPoints(lab_accomplishment: ILabAccomplishment|undefined, subcohortId: string, labId: string, points: number) {
        const reason = "Added on leaderboard";
        const original_manual_points = lab_accomplishment?.manual_points ?? 0;
        if (lab_accomplishment)
            lab_accomplishment.manual_points = points;
        //console.log(`setManualPoints cohortData.id=${cohortData?.id} subcohortId=${subcohortId} labId=${labId}`);
        /*setManualApiState({isLoading: true, errorMessage: null, isUnauthenticated: false});*/
        api.setManualPoints(setManualApiState, cohortData?.id ?? "", subcohortId, labId, points, reason)
        .then((response: ISetManualPointsResponse) => {
            setAddingPoints(null);
            if (response.status === "ok") {
                //We will send an SQS message from the server, that will fix everything else in each frontend instance
                //(including us / the one that made the change)
                //setRewards(response?.rewards ?? null);
            }
            else {
                if (lab_accomplishment)
                    lab_accomplishment.manual_points = original_manual_points;
            }
        })
        .catch((error) => {
            setAddingPoints(null);
            console.error("Error setting manual points:", error);
            if (lab_accomplishment)
                lab_accomplishment.manual_points = original_manual_points;
        });
    }

    function getDisableText(lab:ILab) : string {
        if (lab.disabled) {
            const disableText = lab.disable_text;
            if (typeof disableText === "string") {
                return disableText;
            } else if (Array.isArray(disableText) && (disableText as unknown[]).length > 0) {
                return disableText[0] as string;
            } else {
                return "Lab is disabled";
            }
        }
        return "";
    }


    const separator = <span className={styles.separator}>|</span>;
    const can_write = hasPermission(props.userInfo, [{ permission:"cohort_write_leaderboard", resource : cohortData?.id}]);
    const ctf_data = cohortData?.ctf_data;
    const started = ctf_data?.started ?? false;
    const user_subcohort = props.userInfo.user_cohort_data[cohortData?.code??""]?.subcohort ?? "";
    
    //console.log(`cohort: ${cohortData?.title}`);
    //console.log(`permissions:${JSON.stringify(props.userInfo.permissions)} id=${cohortData?.id}`);

    return <>
        <Loading isLoading={apiState.isLoading}>{!!apiState.errorMessage && !apiState.isUnauthenticated ? (
            <ErrorNotification errorMessage={apiState.errorMessage} />
        ) : (
            <>
                {can_write && (
                    <Loading isLoading={startStopApiState.isLoading} className={styles.startStopLoading}>
                        <div className={`${styles.startStopContainer} ${started ? styles.CTFstarted : styles.CTFstopped}`}>
                            <button
                                type="submit"
                                className={`btn wide ${started ? "primary" : "secondary"} wide}`}
                                onClick={() => startStopCtf(started ? "stop" : "start")}
                            >
                            {started ? "Stop" : "Start"}
                            </button>
                        </div>
                    </Loading>
                )}
                <ReactTooltip id="subcohort-tooltip" html={true} className={styles.subcohortTooltip}/>
                <div className={styles.table}>
                    {cohortData?.subcohorts && subcohortByPoints.map(({ subcohort }) => {
                        const subcohort_rewards = rewards?.[subcohort.id];
                        const score_multiplier_lab_id = ctf_data?.score_multiplier?.[subcohort.id];
                        const subcohort_lab_list = perSubcohortLabList ? perSubcohortLabList[subcohort.id] : labsList; // labsList is fallback
                        //console.log(`${subcohort.id}: started=${started} score_multiplier_lab_id=${score_multiplier_lab_id}`);
                        /*
                        const subcohort_rewards__ = subcohort_rewards ? { ...subcohort_rewards } : undefined;
                        if (subcohort_rewards__) {
                            delete (subcohort_rewards__  as any).achievements;
                            delete (subcohort_rewards__  as any).lab_accomplishments;
                        }
                        console.log(`${subcohort.id}: subcohort_rewards=${JSON.stringify(subcohort_rewards__)}`);
                        */
                        return (
                            <li
                                key={subcohort.id}
                                className={styles.subcohort}
                                data-id={(subcohort.id !== "" ? subcohort.id : "(none)") + "/"}
                            >
                                <div className={styles.subcohortData}>
                                    <div className={styles.subcohort_rewards}>
                                        <div className={styles.subcohortName}>{subcohort.name}</div>
                                        <div className={styles.points}
                                            data-for="subcohort-tooltip"
                                            data-tip={`Total points earned by members of this subcohort, including bonuses and manual points.<br/>`+
                                                      `Base points: ${subcohort_rewards?.lab_points??0}<br/>`+
                                                      `Bonuses: ${subcohort_rewards?.overall_bonus??0}<br/>`+
                                                      ((subcohort_rewards?.manual_points??0)!==0 ? `Points added manually: ${subcohort_rewards?.manual_points??0}` : ``)
                                                    }
                                        >
                                            {subcohort_rewards?.overall_points??0}<span className={styles.pointsLabel}>PTS</span>
                                        </div>
                                        <div className={styles.achievements}>
                                        {Array.isArray(subcohort_rewards?.achievements) && subcohort_rewards?.achievements.map((achievement, idx) => (
                                            <div key={achievement.id}>
                                                {separator}
                                                <span className={`${styles.achievement} ${getAchievementClass(achievement)} ${achievement.achieved ? styles.achievement_achieved : styles.achievement_not_achieved}`}
                                                     data-for="subcohort-tooltip"
                                                     data-tip={achievement.description}
                                                >
                                                    {achievement.badge &&
                                                    <img
                                                        src={`/icons/${achievement.badge}`}
                                                        className={`${styles.achievementBadgeIcon} ${achievement.achieved ? styles.badgeDone : styles.badgeNotDone}`}
                                                        alt=""
                                                    />
                                                    }
                                                    <span className={styles.achievementText}>{achievement.header_text}
                                                        {(achievement.type==="counttags" || achievement.type==="countdone") && !achievement.achieved && ((achievement?.min_count||0)>1) && (
                                                            <span> ({achievement.count}/{achievement.min_count})</span>
                                                        )}
                                                    </span>
                                                </span>
                                            </div>
                                        ))}
                                        </div>
                                    </div>
                                    {/*
                                    {!!memberData && (
                                        <div
                                            className={styles.members}
                                            style={{ cursor: "pointer", userSelect: "none" }}
                                            onClick={() =>
                                                setOpenMembers((prev) => ({
                                                    ...prev,
                                                    [subcohort.id]: !prev[subcohort.id],
                                                    }))
                                                }
                                        >
                                            {!openMembers[subcohort.id] ? (
                                                <span>
                                                    {memberids.length} Members &nbsp;▼
                                                </span>
                                            ): (
                                                <>
                                                    {memberids.map((user_id: string, index) => {
                                                        const userData = memberData[user_id];
                                                        return (
                                                            <div key={`${subcohort.id}/${user_id}`}>
                                                                •&nbsp;{userData?.fullname ?? ""}
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    */}
                                </div>
                                <div className={styles.subcohortLists}>
                                    {/* The "key" attributes makes sure that toggling does not break the tooltip. */}
                                    <ReactTooltip 
                                        id={`${subcohort.id}/lab-tooltip`} html={true} 
                                        className={styles.labTooltip}
                                        key={openLabs[subcohort.id] ? `${subcohort.id}/lab-tooltip-visible` : `${subcohort.id}/lab-tooltip-hidden`}
                                    />
                                    {!!subcohort_lab_list && (
                                        <div
                                            className={styles.labs}
                                        >
                                            <div
                                                className={styles.labsToggle}
                                                onClick={() => {
                                                    setOpenLabs((prev) => ({
                                                        ...prev,
                                                        [subcohort.id]: !prev[subcohort.id],
                                                        }));
                                                }}
                                            >
                                                Labs &nbsp;{openLabs[subcohort.id] ? "▼" : "▲"}
                                            </div>

                                            {!openLabs[subcohort.id] && (
                                                <>
                                                    {subcohort_lab_list.map((lab: ILab, index) => {
                                                        const lab_id = lab.lab_id;
                                                        const lab_accomplishment = subcohort_rewards?.lab_accomplishments?.[lab_id];
                                                        const done = lab_accomplishment?.finished ?? false;
                                                        const lab_points = lab_accomplishment?.lab_points ?? lab.points;
                                                        const actual_points = lab_accomplishment?.actual_points;
                                                        const actual_bonus = lab_accomplishment?.actual_bonus;
                                                        const manual_points = lab_accomplishment?.manual_points ?? 0;

                                                        return (
                                                            <div key={`${subcohort.id}/${lab_id}`}>
                                                                {user_subcohort===subcohort.id ? (
                                                                    <Loading
                                                                        isLoading={ multiplierState.isLoading && 
                                                                                    settingMultiplier?.subcohort_id===subcohort.id &&
                                                                                    settingMultiplier?.lab_id===lab_id}
                                                                        className={styles.scoreMultiplierLoading}
                                                                    >
                                                                        <span className={getScoreMultiplierClass(user_subcohort, subcohort.id, lab, score_multiplier_lab_id, started, settingMultiplier)}
                                                                            data-for={`${subcohort.id}/lab-tooltip`}
                                                                            data-tip={!started && lab_id!==score_multiplier_lab_id ? "Click here to set Score Multiplier and double your points on this lab." : 
                                                                                (done ? "You doubled you score in this lab.": "You will double your score in this lab once you complete it.")}
                                                                            onClick={(e) => !started ? setCtfData(e, "score_multiplier", subcohort.id, lab.lab_id) : null}
                                                                        >x2</span>
                                                                    </Loading>
                                                                ) : (
                                                                    <span className={getScoreMultiplierClass(user_subcohort, subcohort.id, lab, score_multiplier_lab_id, started, settingMultiplier)}
                                                                    >x2</span>
                                                                )}
                                                                {started ?
                                                                <>
                                                                    <span className={lab_accomplishment?.codereview_done ? styles.codereview_done : styles.codereview_not_done}>{lab.codereview ? "✓" : "\u00A0"}</span>
                                                                    <span className={styles.lab_accomplishment_tick}>{done ? "✓" : "\u00A0"}</span>
                                                                </>
                                                                :
                                                                <>
                                                                    <span>{"\u00A0"}</span>
                                                                    <span>{"\u00A0"}</span>
                                                                </>
                                                                }
                                                                <span className={`${styles.labTitle} ${lab.disabled ? styles.labDisabled : ""}`}
                                                                    data-for={`${subcohort.id}/lab-tooltip`}
                                                                    data-tip={getDisableText(lab)}
                                                                >{lab.title ?? `(${lab_id})`}</span>
                                                                {Array.isArray(subcohort_rewards?.achievements) && countCounttags(subcohort_rewards?.achievements||[], lab?.tags||[])>0 && ( 
                                                                    <span className={styles.labAchievements}>
                                                                        {subcohort_rewards?.achievements.map((achievement) => {
                                                                            if (!achievement.tag) return null;
                                                                            if ((lab?.tags || []).includes(achievement.tag || "")) {
                                                                                return (
                                                                                    <img
                                                                                        key={`${subcohort.id}/${lab_id}/achievement/${achievement.id}`}
                                                                                        src={`/icons/${achievement.badge}`}
                                                                                        className={`${styles.achievementBadgeIcon} ${done ? styles.badgeDone : styles.badgeNotDone}`}
                                                                                        alt=""
                                                                                        data-for={`${subcohort.id}/lab-tooltip`}
                                                                                        data-tip={achievement.badge_text}
                                                                                    />
                                                                                );
                                                                            } else if (Array.isArray(lab_accomplishment?.tags) && lab_accomplishment?.tags.includes(achievement.tag || "")) {
                                                                                return (
                                                                                    <img
                                                                                        key={`${subcohort.id}/${lab_id}/achievement/${achievement.id}`}
                                                                                        src={`/icons/${achievement.badge}`}
                                                                                        className={`${styles.achievementBadgeIcon} ${styles.badgeAccomplished}`}
                                                                                        alt=""
                                                                                        data-for={`${subcohort.id}/lab-tooltip`}
                                                                                        data-tip={achievement.badge_text}
                                                                                    />
                                                                                );
                                                                            }
                                                                            return null;
                                                                        })}
                                                                    </span>
                                                                )}
                                                                <span className={styles.labPoints}
                                                                      data-for={`${subcohort.id}/lab-tooltip`}
                                                                      data-tip={done ? `You scored ${actual_points} points for doing the lab${actual_bonus ? (' and '+actual_bonus+' bonus points') : ''}.` : ''}
                                                                >
                                                                    {done ? 
                                                                        <span className={styles.donePoints}>{actual_points ? ""+actual_points : "0"}
                                                                        {actual_bonus ? "+" + actual_bonus : ""}
                                                                        <span className={styles.pointsLabel}>PTS</span>
                                                                        </span>
                                                                    : 
                                                                        <span className={styles.notDonePoints}>{lab_points ? ""+lab_points : ""}
                                                                        <span className={styles.pointsLabel}>PTS</span>
                                                                        </span>
                                                                    }
                                                                {Array.isArray(subcohort_rewards?.achievements) && lab_accomplishment?.bonuses && (
                                                                    subcohort_rewards?.achievements.map((achievement: IAchievement) => {
                                                                        let bonus_points = lab_accomplishment?.bonuses?.[achievement.id];
                                                                        if (bonus_points) {
                                                                            return (
                                                                                <span key={`${subcohort.id}/${lab_id}/bonus/${achievement.id}`}>
                                                                                    <span>&nbsp;+&nbsp;</span>
                                                                                    <span className={`${getAchievementClass(achievement)}`}
                                                                                        data-for={`${subcohort.id}/lab-tooltip`}
                                                                                        data-tip={achievement.bonus_text}
                                                                                    >{""+bonus_points}
                                                                                        <span className={styles.pointsLabel}>{achievement.bonus_suffix}</span>
                                                                                    </span>
                                                                                </span>
                                                                            );
                                                                        }
                                                                        return null;
                                                                    })
                                                                )}
                                                                </span>
                                                                {/*<span>B:{JSON.stringify(lab_accomplishment?.bonuses)}</span>*/}
                                                                {/*<span>{JSON.stringify(lab_accomplishment?.tags)}</span>*/}
                                                                {
                                                                    can_write && addingPoints?.subcohort_id === subcohort.id && addingPoints?.lab_id === lab_id ? (
                                                                        <div className={styles.editPointsContainer}>
                                                                            <Loading isLoading={manualApiState.isLoading} className={styles.editPointsLoading}>
                                                                                <input
                                                                                    type="number"
                                                                                    className={styles.editPointsInput}
                                                                                    value={editingPointsValue}
                                                                                    onChange={e => setEditingPointsValue(e.target.value)}
                                                                                    placeholder="Points"
                                                                                    autoFocus
                                                                                    onKeyDown={e => {
                                                                                        if (e.key === 'Enter') {
                                                                                            setManualPoints(lab_accomplishment, subcohort.id, lab_id, parseInt(editingPointsValue, 10));
                                                                                        } else if (e.key === 'Escape') {
                                                                                            setAddingPoints(null);
                                                                                        }
                                                                                    }}
                                                                                    onBlur={() => setAddingPoints(null)}
                                                                                />
                                                                            </Loading>
                                                                        </div>
                                                                    ) : (
                                                                        manual_points!==0 ? (
                                                                            <span className={styles.manualPoints}
                                                                                  onClick={() => {
                                                                                      setEditingPointsValue(""+manual_points);
                                                                                      setAddingPoints({ subcohort_id: subcohort.id, lab_id });
                                                                                  }}
                                                                                data-for={`${subcohort.id}/lab-tooltip`}
                                                                                data-tip="Points added manually by the Master of Ceremony."
                                                                            >&nbsp;{manual_points>0 ? "+" : ""}&nbsp;{manual_points ? ""+manual_points : "0"}
                                                                                <span className={styles.manualPointsLabel}>MP</span>
                                                                            </span>
                                                                        ) : (can_write && (
                                                                            <img
                                                                                src={`/icons/plus_40x40.png`}
                                                                                className={styles.addScore}
                                                                                alt=""
                                                                                data-for={`${subcohort.id}/lab-tooltip`}
                                                                                data-tip="Manually add or adjust points for this lab."
                                                                                onClick={() => {
                                                                                    setEditingPointsValue("");
                                                                                    setAddingPoints({ subcohort_id: subcohort.id, lab_id });
                                                                                }}
                                                                            />)
                                                                        )
                                                                    )
                                                                }
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    }
                    )}
                </div>
            </>
        )}
        </Loading>
    </>;
}
